import { shapes } from '@joint/plus';
import React, { ChangeEvent, ReactElement, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import Input from '../Input/Input';
import SmartAutocomplete from '../Input/SmartAutocomplete/SmartAutocomplete';
import { useBaseInspector } from './useBaseInspector';

import ReactQuill from 'react-quill';
import * as ParamsService from "../../../../../../../../services/2talk/chatbotResponseParams.service";
import HelperCustomField from '../../HelperCustomField/HelperCustomField';
import { Switch, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface Props {
    cell: shapes.app.Message;
    chatbotFlowId: string;
}

const cellProps = {
    label: ['attrs', 'label', 'text'],
    description: ['attrs', 'description', 'text'],
    icon: ['attrs', 'icon', 'xlinkHref'],
    portLabel: ['attrs', 'portLabel', 'text'],
    integrationParamsId: ['attrs', 'integrationParamsId', 'id'],
    isTextRich: ['attrs', 'isTextRich', 'isTextRich'],
    hasAttachmentParam: ['attrs', 'hasAttachmentParam', 'hasAttachmentParam'],
    integrationAttachmentParamsId: ['attrs', 'integrationAttachmentParamsId', 'id'],
};

const ResponseParamInspector = (props: Props): ReactElement => {

    const { cell, chatbotFlowId } = props;
    const intl = useIntl();

    const [paramsOptions, setParamsOptions] = useState([]);
    const [attachmentParamsOptions, setAttachmentParamsOptions] = useState([]);

    const [label, setLabel] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [integrationParamsId, setIntegrationParamsId] = useState(null);
    const [isTextRich, setIsTextRich] = useState<boolean>(false);
    const [hasAttachmentParam, setHasAttachmentParam] = useState<boolean>(false);
    const [integrationAttachmentParamsId, setIntegrationAttachmentParamsId] = useState(null);

    const assignFormFields = useCallback((): void => {
        setLabel(cell.prop(cellProps.label));
        setDescription(cell.prop(cellProps.description));
        setIntegrationParamsId(cell.prop(cellProps.integrationParamsId));
        setIsTextRich(cell.prop(cellProps.isTextRich));
        setHasAttachmentParam(cell.prop(cellProps.hasAttachmentParam));
        setIntegrationAttachmentParamsId(cell.prop(cellProps.integrationAttachmentParamsId));
    }, [cell]);

    const changeCellProp = useBaseInspector({ cell, assignFormFields });

    const fetchParams = async () => {
        const params = await ParamsService.getAll();
        setParamsOptions(params?.data ?? []);
    }

    const fetchAttachmentParams = async () => {
        const params = await ParamsService.getAllAttachmentParams();
        setAttachmentParamsOptions(params?.data ?? []);
    }

    useEffect(() => {
        const fetchAllParams = async () => {
            await fetchParams();
            await fetchAttachmentParams();
        }

        fetchAllParams();
    }, []);

    const handleChangeHasAttachmentParam = (checked: boolean) => {
        changeCellProp(cellProps.hasAttachmentParam, checked);

        if (checked == false) {
            setIntegrationAttachmentParamsId(null);
            changeCellProp(cellProps.integrationAttachmentParamsId, null);
        }
    }

    return (
        <>

            <label>Label
                <Input type="text"
                       placeholder="Enter label"
                       value={label}
                       disabled={true}
                />
            </label>

            <div style={{ marginBottom: '8px', marginTop: '8px'}}>
                <Switch
                    value={isTextRich}
                    checked={isTextRich}
                    onChange={(e:any) => changeCellProp(cellProps.isTextRich, e?.target?.checked)}     
                />
                <label>É um texto rico?</label>
            </div>

            {
                isTextRich
                ? (
                    <label>Descrição
                        <div style={{ resize: "vertical", overflow: "auto", height: "250px", marginBottom: "24px", border: "1px solid #ccc" }}>
                            <ReactQuill
                                style={{ height: '100%' }}
                                name="description"
                                fullWidth
                                variant="outlined"
                                id="descriprion"
                                value={description}
                                onChange={(value: any) => changeCellProp(cellProps.description, value)}
                            />
                        </div>
                    </label>
                )
                : (
                    <label>Descrição
                        <Input 
                            type="text"
                            placeholder="Enter description"
                            value={description}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => changeCellProp(cellProps.description, e.target.value)}
                        />
                    </label>
                )
            }

            <HelperCustomField
                isRichText={isTextRich}
                description={description}
                param1={cellProps.description}
                onChangeDescription={changeCellProp}
                chatbotFlowId={chatbotFlowId}
                hideChatTab
            />

            {/* Item #4.6 (09/10/2026) — Relabel UX. A caixa SEMPRE pergunta ao cliente e
                salva a resposta no param escolhido; antes o inspector só mostrava "Parâmetro"
                sem explicar o papel. Agora o banner + labels deixam o fluxo óbvio. Zero
                mudança funcional — mesmo shape, mesmo backend, mesmo endpoint. */}
            <div style={{
                marginTop: 16, padding: '8px 10px', borderRadius: 6,
                background: '#EEF4FF', color: '#1D3557', fontSize: 13,
                display: 'flex', alignItems: 'flex-start', gap: 8
            }}>
                <InfoOutlinedIcon fontSize="small" style={{ marginTop: 2, color: '#277AFE' }}/>
                <span>
                    {intl.formatMessage({
                        id: 'responseparam.hint',
                        defaultMessage: 'Esta caixa pergunta ao cliente e salva a resposta no parâmetro escolhido abaixo. Para usá-la em caixas Condição depois, consulte o parâmetro pelo nome.'
                    })}
                </span>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12 }}>
                {intl.formatMessage({ id: 'responseparam.target.label', defaultMessage: 'Resposta do cliente salva em' })}
                <Tooltip title={intl.formatMessage({ id: 'responseparam.target.help', defaultMessage: 'Parâmetro onde a mensagem digitada pelo cliente será gravada. Depois pode ser lida com %nome_do_param%.' })}>
                    <InfoOutlinedIcon fontSize="small" style={{ color: '#777', cursor: 'help' }}/>
                </Tooltip>
            </label>
            <SmartAutocomplete
                value={integrationParamsId}
                onChange={(e) => changeCellProp(cellProps.integrationParamsId, e.target.value)}
                options={paramsOptions}
            />

            <div style={{ marginBottom: '8px', marginTop: '16px' }}>
                <Switch
                    value={hasAttachmentParam}
                    checked={hasAttachmentParam}
                    onChange={(e:any) => handleChangeHasAttachmentParam(e.target.checked)}
                />
                <label>{intl.formatMessage({ id: 'responseparam.attachment.toggle', defaultMessage: 'Permitir anexo junto da resposta?' })}</label>
            </div>

            {hasAttachmentParam && (
                <>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {intl.formatMessage({ id: 'responseparam.attachmentTarget.label', defaultMessage: 'Anexo é salvo em' })}
                        <Tooltip title={intl.formatMessage({ id: 'responseparam.attachmentTarget.help', defaultMessage: 'Parâmetro onde o arquivo anexado pelo cliente será registrado (link + metadados).' })}>
                            <InfoOutlinedIcon fontSize="small" style={{ color: '#777', cursor: 'help' }}/>
                        </Tooltip>
                    </label>
                    <SmartAutocomplete
                        value={integrationAttachmentParamsId}
                        onChange={(e) => changeCellProp(cellProps.integrationAttachmentParamsId, e.target.value)}
                        options={attachmentParamsOptions}
                    />
                </>
            )}

        </>
    );
};

export default ResponseParamInspector;
